import React from "react";
import {
  Container,
  Paper,
  Skeleton,
  Autocomplete,
  TextField,
  Stepper,
  Step,
  StepLabel,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTemplateUploadPage } from "./useTemplateUploadPage";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import SignOffDialog from "./SignOffDialog";
import TemplatePreviewDialog from "./TemplatePreviewDialog";
import UploadStep from "./UploadStep";
import ProcessingStep from "./ProcessingStep";
import ReviewStep from "./ReviewStep";
import CompleteStep from "./CompleteStep";
import TemplatesTable from "./TemplatesTable";

const WORKFLOW_STEPS = ["Upload File", "Processing", "Review Template", "Complete"];
const WORKFLOW_KEYS = ["upload", "processing", "review", "complete"] as const;

const TemplateUploadPage: React.FC = () => {
  const theme = useTheme();
  const state = useTemplateUploadPage();

  return (
    <Container sx={{ overflow: "auto" }}>

      <DeleteConfirmDialog
        open={state.deleteDialogOpen}
        template={state.templateToDelete}
        isDeleting={state.isDeleting}
        onClose={() => state.setDeleteDialogOpen(false)}
        onConfirm={state.handleDeleteConfirm}
      />

      <Grid
        container
        spacing={3}
        direction="column"
        sx={{
          display: "flex",
          alignItems: "stretch",
          py: 2,
        }}
      >
        {/* Station Selector */}
        {state.stationsLoading && (
          <Grid size={{ xs: 12 }}>
            <Skeleton
              variant="rounded"
              width={360}
              height={40}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
        )}
        {!state.stationsLoading && state.stations.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              value={
                state.stations.find(
                  (s) => s.stationId === state.selectedStationId,
                ) ?? undefined
              }
              onChange={(_, value) => {
                if (value) {
                  state.setSelectedStationId(value.stationId);
                  state.setTemplates([]);
                  state.setPage(0);
                }
              }}
              options={state.stations}
              getOptionLabel={(option) => option.stationName}
              isOptionEqualToValue={(option, value) =>
                option.stationId === value.stationId
              }
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Station"
                  size="small"
                  sx={{
                    maxWidth: 360,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: alpha(theme.palette.divider, 0.3),
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              )}
              slotProps={{
                listbox: {
                  style: { maxHeight: 48 * 10, overflow: "auto" },
                },
              }}
            />
          </Grid>
        )}

        {/* Workflow Section */}
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: "background.paper",
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            }}
          >
            <Stepper
              activeStep={WORKFLOW_KEYS.indexOf(state.workflowStep)}
              sx={{ mb: 4 }}
            >
              {WORKFLOW_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {state.workflowStep === "upload" && (
              <UploadStep
                uploadQueue={state.uploadQueue}
                isDragOver={state.isDragOver}
                fileInputRef={state.fileInputRef}
                onFileSelect={state.handleFileSelect}
                onDragOver={state.handleDragOver}
                onDragLeave={state.handleDragLeave}
                onDrop={state.handleDrop}
                onRemoveFromQueue={state.handleRemoveFromQueue}
                onSubmitForProcessing={state.handleSubmitForProcessing}
              />
            )}

            {state.workflowStep === "processing" && (
              <ProcessingStep
                processingError={state.processingError}
                onBackToUpload={() => {
                  state.setProcessingError(null);
                  state.setJobId(null);
                  state.setWorkflowStep("upload");
                }}
              />
            )}

            {state.workflowStep === "review" && (
              <ReviewStep
                reviewContent={state.reviewContent}
                generatedExamples={state.generatedExamples}
                messages={state.templateChat.messages}
                isLoading={state.templateChat.isLoading}
                chatContainerRef={state.chatContainerRef}
                onChatScroll={state.handleChatScroll}
                showScrollArrow={state.showScrollArrow}
                onScrollToBottom={state.scrollChatToBottom}
                onHideScrollArrow={() => state.setShowScrollArrow(false)}
                onSendMessage={state.templateChat.sendMessage}
                onBack={() => {
                  state.setWorkflowStep("upload");
                }}
                onSignOff={() => state.setSignOffDialogOpen(true)}
              />
            )}

            {state.workflowStep === "complete" && (
              <CompleteStep onCreateNew={state.resetWorkflow} />
            )}
          </Paper>
        </Grid>

        <SignOffDialog
          open={state.signOffDialogOpen}
          onClose={() => state.setSignOffDialogOpen(false)}
          onConfirm={state.handleSignOff}
        />

        <TemplatePreviewDialog
          template={state.previewTemplate}
          onClose={() => state.setPreviewTemplate(null)}
        />

        {/* Templates Table Section */}
        <Grid size={{ xs: 12 }}>
          <TemplatesTable
            templates={state.templates}
            filteredTemplates={state.filteredTemplates}
            paginatedTemplates={state.paginatedTemplates}
            isLoading={state.isLoading}
            page={state.page}
            rowsPerPage={state.rowsPerPage}
            templateSearch={state.templateSearch}
            editingTemplateId={state.editingTemplateId}
            editingName={state.editingName}
            onSearchChange={(value) => {
              state.setTemplateSearch(value);
              state.setPage(0);
            }}
            onPageChange={state.handleChangePage}
            onRowsPerPageChange={state.handleChangeRowsPerPage}
            onRefresh={() => {
              state.fetchTemplates();
            }}
            onPreview={state.setPreviewTemplate}
            onDownload={state.handleDownload}
            onDelete={state.handleDeleteClick}
            onStartEditing={(template) => {
              state.setEditingTemplateId(template.id);
              state.setEditingName(template.fileName || template.name || "");
            }}
            onEditingNameChange={state.setEditingName}
            onRename={state.handleRename}
            onCancelEditing={() => state.setEditingTemplateId(null)}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default TemplateUploadPage;
